import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { reportsService, LeaveSummaryRow, LeaveSummaryReportFilters } from '../../services/api/reports';
import { departmentsService } from '../../services/api/departments';
import { Department } from '../../types';
import './LeaveSummaryReport.css';

// Helper function to get current month date range
const getCurrentMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0); // Last day of current month
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

const LeaveSummaryReport: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LeaveSummaryRow[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  
  // Get current month start and end dates
  const monthRange = useMemo(() => getCurrentMonthRange(), []);
  
  const [filters, setFilters] = useState<LeaveSummaryReportFilters>({
    startDate: monthRange.startDate,
    endDate: monthRange.endDate,
    includeSubtree: false,
  });

  useEffect(() => {
    loadDepartments();
    loadReport();
  }, []);

  useEffect(() => {
    loadReport();
  }, [filters]);

  const loadDepartments = async () => {
    try {
      const depts = await departmentsService.getAll();
      setDepartments(depts);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const result = await reportsService.getLeaveSummaryReport(filters);
      setData(result);
    } catch (error) {
      console.error('Failed to load leave summary report:', error);
      alert(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof LeaveSummaryReportFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      await reportsService.exportLeaveSummaryReport(filters);
    } catch (error) {
      console.error('Failed to export report:', error);
      alert(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const totalPaid = data.reduce((sum, row) => sum + row.totalPaidDays, 0);
  const totalUnpaid = data.reduce((sum, row) => sum + row.totalUnpaidDays, 0);
  const totalLate = data.reduce((sum, row) => sum + row.lateSubmissionCount, 0);

  return (
    <div className="leave-summary-report">
      <div className="report-header">
        <button className="back-button" onClick={() => navigate('/reports')}>
          ← {t('common.back')} {t('reports.title')}
        </button>
        <div>
          <h1>{t('reports.leaveSummary.title')}</h1>
          <p>{t('reports.leaveSummary.description')}</p>
        </div>
      </div>

      <div className="report-filters">
        <div className="filter-group">
          <label>{t('reports.filters.startDate')}</label>
          <input
            type="date"
            value={filters.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>{t('reports.filters.endDate')}</label>
          <input
            type="date"
            value={filters.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>{t('reports.filters.department')}</label>
          <select
            value={filters.departmentId || ''}
            onChange={(e) =>
              handleFilterChange('departmentId', e.target.value ? Number(e.target.value) : undefined)
            }
          >
            <option value="">{t('reports.filters.allDepartments')}</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>
            <input
              type="checkbox"
              checked={filters.includeSubtree || false}
              onChange={(e) => handleFilterChange('includeSubtree', e.target.checked)}
            />
            {t('reports.filters.includeSubtree')}
          </label>
        </div>
        <button className="export-button" onClick={handleExport} disabled={loading}>
          {loading ? t('common.exporting') : t('common.export')}
        </button>
      </div>

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <>
          <div className="summary-cards">
            <div className="summary-card">
              <div className="summary-label">{t('reports.leaveSummary.totalPaidDays')}</div>
              <div className="summary-value">{totalPaid.toFixed(2)}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">{t('reports.leaveSummary.totalUnpaidDays')}</div>
              <div className="summary-value">{totalUnpaid.toFixed(2)}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">{t('reports.leaveSummary.lateSubmissions')}</div>
              <div className="summary-value">{totalLate}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">{t('reports.leaveSummary.totalEmployees')}</div>
              <div className="summary-value">{data.length}</div>
            </div>
          </div>

          <div className="report-table-container">
            <table className="report-table">
              <thead>
                <tr>
                  <th>{t('reports.leaveSummary.employeeCode')}</th>
                  <th>{t('reports.leaveSummary.employeeName')}</th>
                  <th>{t('reports.leaveSummary.department')}</th>
                  <th>{t('reports.leaveSummary.paidDays')}</th>
                  <th>{t('reports.leaveSummary.unpaidDays')}</th>
                  <th>{t('reports.leaveSummary.lateCount')}</th>
                  <th>{t('reports.leaveSummary.totalRequests')}</th>
                  <th>{t('reports.leaveSummary.currentBalance')}</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="no-data">
                      {t('common.noData')}
                    </td>
                  </tr>
                ) : (
                  data.map((row) => (
                    <tr key={row.employeeId}>
                      <td>{row.employeeCode}</td>
                      <td>{row.employeeName}</td>
                      <td>{row.departmentName}</td>
                      <td className="number-cell">{row.totalPaidDays.toFixed(2)}</td>
                      <td className="number-cell">{row.totalUnpaidDays.toFixed(2)}</td>
                      <td className="number-cell">{row.lateSubmissionCount}</td>
                      <td className="number-cell">{row.totalRequests}</td>
                      <td className="number-cell">{row.currentBalance.toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default LeaveSummaryReport;
